import type { PDFFont, rgb } from 'pdf-lib';

/**
 * Font-Auswahl, Textumbruch und Zeilen-Styling.
 *
 * Aus `@kit/self-disclosure` herausgelöst (Spec `2026-07-30-legal-pdf-renderer.md`, PR-2),
 * Logik unverändert. Einzige bewusste Änderung: `styledLines` las bisher die Modul-Konstanten
 * `BODY_SIZE`, `TEXT_COLOR` und `LINE_GAP` des Selbstauskunft-Renderers direkt. Die bleiben
 * dort fachlich verankert, deshalb kommen sie hier als expliziter `StyleDefaults`-Parameter
 * herein (Review-Finding F8). Der Aufrufer übergibt exakt seine bisherigen Werte — sonst
 * wäre der Umzug nicht verhaltensneutral.
 */

type RgbColor = ReturnType<typeof rgb>;

export type FontStyle = 'regular' | 'bold' | 'italic';

export interface Fonts {
  regular: PDFFont;
  bold: PDFFont;
  italic: PDFFont;
}

export function fontFor(fonts: Fonts, style: FontStyle): PDFFont {
  switch (style) {
    case 'regular':
      return fonts.regular;
    case 'bold':
      return fonts.bold;
    case 'italic':
      return fonts.italic;
  }
}

/** Eine vorumbrochene, vorgestylte Textzeile (einzeln und in Tabellenzellen). */
export interface CellLine {
  text: string;
  size: number;
  font: FontStyle;
  color: RgbColor;
  height: number;
}

export interface LineStyle {
  size?: number;
  font?: FontStyle;
  color?: RgbColor;
}

/** Werte, die der Aufrufer für nicht gesetzte `LineStyle`-Felder vorgibt (F8). */
export interface StyleDefaults {
  size: number;
  color: RgbColor;
  /** Zeilenabstand: `height = size + lineGap`. */
  lineGap: number;
}

/**
 * Längster Präfix von `word`, der in `maxWidth` passt (Binärsuche über
 * `widthOfTextAtSize`). Gibt mindestens 1 zurück, damit der Aufrufer immer
 * Fortschritt macht — auch wenn eine einzelne Glyphe breiter als die Spalte wäre.
 */
function longestFittingPrefix(
  word: string,
  size: number,
  font: PDFFont,
  maxWidth: number,
): number {
  let low = 1;
  let high = word.length - 1; // das ganze Wort überschreitet maxWidth bekanntlich
  let best = 1;

  while (low <= high) {
    const mid = Math.floor((low + high) / 2);

    if (font.widthOfTextAtSize(word.slice(0, mid), size) <= maxWidth) {
      best = mid;
      low = mid + 1;
    } else {
      high = mid - 1;
    }
  }

  return best;
}

/**
 * Greedy Word-Wrap. Ein Wort, das breiter als `maxWidth` ist, wird zeichenweise
 * hart umbrochen statt über die Spalte hinauszulaufen. Gilt für ALLE Spaltenbreiten —
 * Messung und Zeichnung nutzen dieselbe Funktion.
 */
export function wrapText(
  text: string,
  size: number,
  font: PDFFont,
  maxWidth: number,
): string[] {
  // Wache gegen eine nicht-positive Spaltenbreite. Bei `maxWidth < 0` hängt die
  // Schleife unten ENDLOS: sobald `rest` leer ist, gilt `widthOfTextAtSize('') = 0`,
  // was immer noch `> maxWidth` ist; `longestFittingPrefix('')` liefert 1, `''.slice(1)`
  // bleibt `''` — und `lines.push('')` füllt den Speicher, bis der Prozess stirbt.
  // Ein hängender Build ohne Fehlermeldung ist die teuerste Fehlerform, die dieser
  // zur Build-Zeit laufende Renderer haben kann.
  //
  // Rein additiv: heute übergibt kein Aufrufer `maxWidth <= 0` (die Spaltenregel in
  // `legal-docs/render-pdf.ts` hat eine Untergrenze). Die Wache schützt JEDEN künftigen
  // Aufrufer und ändert an keinem bestehenden Ergebnis etwas.
  if (maxWidth <= 0) return [text];

  const words = text.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = '';

  const flush = () => {
    if (current) {
      lines.push(current);
      current = '';
    }
  };

  for (const word of words) {
    if (font.widthOfTextAtSize(word, size) > maxWidth) {
      flush();

      let rest = word;

      while (font.widthOfTextAtSize(rest, size) > maxWidth) {
        const cut = longestFittingPrefix(rest, size, font, maxWidth);
        lines.push(rest.slice(0, cut));
        rest = rest.slice(cut);
      }

      current = rest;
      continue;
    }

    const candidate = current ? `${current} ${word}` : word;

    if (font.widthOfTextAtSize(candidate, size) > maxWidth) {
      flush();
      current = word;
    } else {
      current = candidate;
    }
  }

  flush();

  return lines.length > 0 ? lines : [''];
}

/**
 * Breite des breitesten unteilbaren (whitespace-getrennten) Tokens eines Textes.
 *
 * Das Gegenstück zu `wrapText`: Wer eine Spalte so bemessen will, dass darin kein Wort
 * zeichenweise zerbricht, braucht genau diese Zahl. Leerer Text ⇒ 0.
 */
export function widestTokenWidth(
  text: string,
  size: number,
  font: PDFFont,
): number {
  let widest = 0;

  for (const token of text.split(/\s+/)) {
    if (!token) continue;

    widest = Math.max(widest, font.widthOfTextAtSize(token, size));
  }

  return widest;
}

/** Umbricht einen Text in gestylte Zellzeilen für eine gegebene Spaltenbreite. */
export function styledLines(
  text: string,
  fonts: Fonts,
  maxWidth: number,
  defaults: StyleDefaults,
  style: LineStyle = {},
): CellLine[] {
  const size = style.size ?? defaults.size;
  const font = style.font ?? 'regular';
  const color = style.color ?? defaults.color;

  return wrapText(text, size, fontFor(fonts, font), maxWidth).map((line) => ({
    text: line,
    size,
    font,
    color,
    height: size + defaults.lineGap,
  }));
}
